/**
 * Keyboard input (R2 §1.1): ←/→ or A/D steer, Space jump, W/↑ accelerate,
 * S/↓ brake (supersedes R1's W/↑-as-jump and the provisional speed nudge),
 * R retry, Esc pause.
 */
import type { InputFrame } from "./sim";

export class KeyboardInput {
  private left = false;
  private right = false;
  private jump = false;
  private accel = false;
  private brake = false;
  private retryQueued = false;
  private pauseQueued = false;
  private anyKeyQueued = false;

  attach(target: Window): void {
    target.addEventListener("keydown", this.onKeyDown);
    target.addEventListener("keyup", this.onKeyUp);
  }

  detach(target: Window): void {
    target.removeEventListener("keydown", this.onKeyDown);
    target.removeEventListener("keyup", this.onKeyUp);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!e.repeat) this.anyKeyQueued = true;
    switch (e.code) {
      case "ArrowLeft":
      case "KeyA":
        this.left = true;
        e.preventDefault();
        break;
      case "ArrowRight":
      case "KeyD":
        this.right = true;
        e.preventDefault();
        break;
      case "Space":
        this.jump = true;
        e.preventDefault();
        break;
      case "KeyW":
      case "ArrowUp":
        this.accel = true;
        e.preventDefault();
        break;
      case "KeyS":
      case "ArrowDown":
        this.brake = true;
        e.preventDefault();
        break;
      case "KeyR":
        if (!e.repeat) this.retryQueued = true;
        break;
      case "Escape":
        if (!e.repeat) this.pauseQueued = true;
        break;
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case "ArrowLeft":
      case "KeyA":
        this.left = false;
        break;
      case "ArrowRight":
      case "KeyD":
        this.right = false;
        break;
      case "Space":
        this.jump = false;
        break;
      case "KeyW":
      case "ArrowUp":
        this.accel = false;
        break;
      case "KeyS":
      case "ArrowDown":
        this.brake = false;
        break;
    }
  };

  /** Current held state as a sim frame. */
  frame(): InputFrame {
    return {
      steer: (this.right ? 1 : 0) - (this.left ? 1 : 0),
      jumpHeld: this.jump,
      accel: this.accel,
      brake: this.brake,
    };
  }

  consumeRetry(): boolean {
    const v = this.retryQueued;
    this.retryQueued = false;
    return v;
  }

  consumePause(): boolean {
    const v = this.pauseQueued;
    this.pauseQueued = false;
    return v;
  }

  consumeAnyKey(): boolean {
    const v = this.anyKeyQueued;
    this.anyKeyQueued = false;
    return v;
  }
}
